
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getUserResumes, getResumeComments } from "../api/resume";
import { log } from "../utils/log_utils";
import { useLocation } from "react-router-dom";

const ResumesContext = createContext();
export const useResumesContext = () => useContext(ResumesContext);

export const ResumesProvider = ({ children }) => {

  const location = useLocation()

  const [resumes, setResumes] = useState(() => {
    const stored = localStorage.getItem("resumes");
    return stored ? JSON.parse(stored) : [];
  });
  const [lastFetched, setLastFetched] = useState(() => {
    const stored = localStorage.getItem("resumes_fetched_at");
    return stored ? parseInt(stored) : null;
  });

  // --- detailed map { resumeId: detailObj }
  const [resumeDetails, setResumeDetails] = useState(() => {
    const stored = localStorage.getItem("resume_details");
    return stored ? JSON.parse(stored) : {};
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearResumes = useCallback(() => {
    setResumes([]);
    setResumeDetails({});
    setLastFetched(null);
    localStorage.removeItem("resumes");
    localStorage.removeItem("resume_details");
    localStorage.removeItem("resumes_fetched_at");
  }, []);

  // refresh lightweight list
  const refreshResumes = useCallback(async () => {
    setLoading(true);
    clearResumes()
    const { success, resp } = await getUserResumes();
    if (success) {
      setResumes(resp);
      setLastFetched(Date.now());
      localStorage.setItem("resumes", JSON.stringify(resp));
      localStorage.setItem("resumes_fetched_at", Date.now().toString());
      setError(null);
    } else {
      setError(resp);
      clearResumes();
    }
    setLoading(false);
  }, [clearResumes]);

  // fetch details for a single resume
  const fetchResumeDetail = useCallback(async (resumeId, force = false) => {
    if (!force && resumeDetails[resumeId]) return resumeDetails[resumeId];
  
    const { success, resp } = await getResumeComments(resumeId);
    if (success) {
      setResumeDetails(prev => {
        const updated = { ...prev, [resumeId]: resp || [] };
        localStorage.setItem("resume_details", JSON.stringify(updated));
        return updated;
      });
      return resp;
    }
    return null;
  }, [resumeDetails]);

  // initial load
  useEffect(() => {
    if(location.pathname !== "/myresumes") return;
  
    async function loadResumes() {
      setLoading(true);
      try {
        await refreshResumes();
      } catch (err) {
        log("failed to load resumes", err, "resumes-context");
        setError("failed to load resume");
        clearResumes();
      }
      setLoading(false);
    }
  
    loadResumes();
  }, [location.pathname, refreshResumes, clearResumes]);


  return (
    <ResumesContext.Provider
      value={{
        resumes,
        resumeDetails,
        error,
        loading,
        lastFetched,
        refreshResumes,
        fetchResumeDetail,
        clearResumes,
      }}
    >
      {children}
    </ResumesContext.Provider>
  );
};
